import { Command } from 'commander';
import chalk from 'chalk';
import fs from 'fs-extra';
import { join, dirname, basename } from 'path';
import { execSync } from 'child_process';
import inquirer from 'inquirer';
import { logger } from '../lib/logger.js';
import { findApplicableMigrations } from '../migrations/registry.js';
import { glob } from 'glob';
import { purgeRalphProjectArtifacts, updateCommands } from '../lib/installer.js';
import { configureClaudeEnvironment, removeMcpServerRegistration } from '../lib/vscode-settings.js';
import { REQUIRED_PACKAGES, resolveLockedFamilyVersion } from '../lib/required-packages.js';
import {
  MIGRATION_IGNORE_GLOBS,
  computeUpgradeOutcome,
  insertExportDatasetsSeam,
  EXPORT_DATASETS_BEGIN,
  stripPlatformAdminFallbacks,
  classifyAdminModuleFallback,
  ADMIN_FALLBACK_SPECIFIER,
} from '../lib/upgrade-helpers.js';
import { parseNuGetDowngrades } from '../lib/nuget-downgrade.js';
import { getNpmVersions } from '../lib/npm-registry.js';
import { getNuGetVersions } from '../lib/nuget-registry.js';
import {
  syncDockerFiles,
  logDockerFileResult,
  logSkippedModifiedSummary,
  ADDITIONAL_CERTIFICATE_SLOT_BLOCK,
  DOCKER_SYNC_FILES,
} from '../lib/docker-sync.js';
import { readProjectSmartStackVersion, supportsEmptyCertificateSlot } from '../lib/smartstack-version.js';
import { loadInitState, saveInitState, createCompletedInitState, recordFile, computeHash } from '../lib/file-tracker.js';
import {
  insertVersionPrefix,
  buildRootPackageJson,
  resolveSeedVersion,
  ensureVersionField,
} from '../lib/client-version.js';
import { backfillDockerDataProtection } from '../lib/dataprotection-provision.js';
import { DOCKER_ENV_REL_PATH } from '../lib/docker-env.js';
import { runUpgradePreflight, planCodemods, type RegistryClient, type UpgradePlan, type JsonSyncPlan, SOCLE_NPM_NAME } from '../lib/upgrade-plan.js';
import { BackupSet, type BackupManifest, type RestoreReport } from '../lib/upgrade-backup.js';
import { planGitattributes } from '../lib/project-gitattributes.js';
import { checkGitSafety, type ConfirmFn } from '../lib/upgrade-git-guard.js';
import {
  getCliVersion,
  readInstalledSkillsVersion,
  resolveCliPackageRoot,
  serializeProjectConfig,
  withGenerator,
  withLastUpgrade,
  PROJECT_CONFIG_REL,
  UPGRADE_REPORT_REL,
  type ProjectConfig,
  type UpgradeStatus,
} from '../lib/project-versions.js';
import { planVerifySteps, runVerify, runProjectRegen, type RegenStepResult, type VerifyResult } from '../lib/upgrade-verify.js';
import { findingLines, modulesToResume, runLifecycleAudit, UPGRADE_RULES, type LifecycleBridgeResult, type ModuleToResume } from '../lib/lifecycle-bridge.js';

export interface UpgradeOptions {
  preview?: boolean;
  dryRun?: boolean;
  allPackages?: boolean;
  forceDocker?: boolean;
  /** Explicit SmartStack target version (`--version`). */
  version?: string;
  allowDowngrade?: boolean;
  allowDirty?: boolean;
  allowProtectedBranch?: boolean;
  allowCommentLoss?: boolean;
  skipSkills?: boolean;
  skipVerify?: boolean;
  /** `--no-regen` sets it to false. */
  regen?: boolean;
  /** `--restore` (true = latest) or `--restore <id>`. */
  restore?: string | boolean;
  listBackups?: boolean;
}

/** Injection points — the defaults are the real network, terminal and cwd. */
export interface UpgradeDeps {
  projectDir?: string;
  registry?: RegistryClient;
  isTTY?: boolean;
  confirm?: ConfirmFn;
}

export type UpgradeRunStatus = 'aborted' | 'dry-run' | 'ok' | 'partial' | 'restored' | 'listed';

export interface UpgradeRunResult {
  status: UpgradeRunStatus;
  exitCode: number;
  plan?: UpgradePlan;
  backupId?: string | null;
}

interface ProjectUpgradeInfo {
  csprojPath: string;
  relPath: string;
  currentVersion: string | null;
  /** Cached .csproj content to avoid re-reads */
  csprojContent: string;
}

interface PackageInfo {
  name: string;
  currentVersion: string;
}

interface UpgradeResult {
  nugetUpgraded: number;
  nugetSkipped: number;
  nugetFailed: number;
  otherPkgUpdated: number;
  otherPkgSkipped: number;
  otherPkgFailed: number;
  npmUpgraded: boolean;
  npmSkipped: boolean;
  npmFailed: boolean;
  npmOtherUpdated: boolean;
  configSynced: number;
  claudeSettingsSynced: number;
  ralphPurged: boolean;
  dockerSynced: number;
  /** Docker files left untouched because the client modified them locally. */
  dockerSkippedModified: string[];
  /** Keys back-filled into docker-images/.env (DataProtection certificate, JWT secret). */
  dockerSecretsProvisioned: string[];
  /** The docker compose maps no DataProtection certificate (modified locally, so not updated). */
  dockerComposeLacksCertificate: boolean;
  /** Where RequireEncryptedKeyRing=false is still set — the platform refuses it outside dev. */
  keyRingOptOuts: string[];
  testFrontendSynced: number;
  /** Locale bundles recovered from the phantom `web/<app>-web` tree (pre-5.11 scaffold-component bug). */
  i18nBundlesRelocated: number;
  /** Platform-admin fallback registrations / orphan placeholder removed (aggregator ≤ 5.12). */
  adminFallbacksCleaned: number;
  residualsCleaned: number;
  initStateFixed: boolean;
  requiredPkgAdded: number;
  requiredPkgFailed: number;
  /** True when a `dotnet restore` failed — the .csproj was edited but won't restore. */
  restoreFailed: boolean;
  /** Direct references auto-bumped to clear an NU1605 downgrade (e.g. EF Core 10.0.5 → 10.0.7). */
  packagesAligned: number;
  /** Packages NuGet still reports as downgrades after auto-repair — feeds the remediation hint. */
  unresolvedDowngrades: string[];
  programCsIssues: string[];
  codemodsApplied: number;
  verifyFailed: boolean;
}

const PACKAGE_ROOT = resolveCliPackageRoot() ?? dirname(__dirname);
const TEMPLATES_DIR = join(PACKAGE_ROOT, 'templates', 'project');

const defaultRegistry: RegistryClient = {
  nugetVersions: getNuGetVersions,
  npmVersions: getNpmVersions,
};

const defaultConfirm: ConfirmFn = async (question) => {
  const { proceed } = await inquirer.prompt([{ type: 'confirm', name: 'proceed', message: question, default: false }]);
  return proceed === true;
};

/**
 * Reads the current SmartStack package version from a .csproj file.
 * Returns null if not found.
 */
function readPackageVersion(csprojContent: string, packageName: string): string | null {
  // Match: <PackageReference Include="SmartStack" Version="2.0.0" />
  // or:    <PackageReference Include="SmartStack" Version="2.0.0-preview.380" />
  const regex = new RegExp(
    `<PackageReference\\s+Include="${packageName}"\\s+Version="([^"]+)"`,
    'i'
  );
  const match = csprojContent.match(regex);
  return match ? match[1] : null;
}

/**
 * Reads ALL PackageReferences from a .csproj file.
 */
function readAllPackageReferences(csprojContent: string): PackageInfo[] {
  const regex = /<PackageReference\s+Include="([^"]+)"\s+Version="([^"]+)"/gi;
  const packages: PackageInfo[] = [];
  let match;
  while ((match = regex.exec(csprojContent)) !== null) {
    packages.push({ name: match[1], currentVersion: match[2] });
  }
  return packages;
}

/**
 * Finds all .csproj files in the src/ directory.
 * Returns all projects regardless of whether they reference SmartStack.
 */
async function findAllProjects(projectDir: string): Promise<ProjectUpgradeInfo[]> {
  const srcDir = join(projectDir, 'src');
  if (!await fs.pathExists(srcDir)) {
    return [];
  }

  const projects: ProjectUpgradeInfo[] = [];
  const folders = await fs.readdir(srcDir);

  for (const folder of folders) {
    const folderPath = join(srcDir, folder);
    const stat = await fs.stat(folderPath);

    if (stat.isDirectory()) {
      const csprojFiles = (await fs.readdir(folderPath)).filter(f => f.endsWith('.csproj'));

      for (const csproj of csprojFiles) {
        const csprojPath = join(folderPath, csproj);
        const content = await fs.readFile(csprojPath, 'utf-8');

        // Read SmartStack version if present (null if not referenced)
        const currentVersion = readPackageVersion(content, 'SmartStack');
        projects.push({
          csprojPath,
          relPath: csprojPath.replace(projectDir, '').replace(/^[/\\]/, ''),
          currentVersion,
          csprojContent: content,
        });
      }
    }
  }

  return projects;
}

function execCommand(command: string, cwd?: string, dryRun = false): void {
  if (dryRun) {
    logger.info(`[DRY RUN] ${command}`);
    return;
  }

  try {
    execSync(command, { cwd, stdio: 'inherit' });
  } catch {
    throw new Error(`Command failed: ${command}`);
  }
}

/**
 * Tries to execute a command and returns success status instead of throwing
 */
function tryExecCommand(command: string, cwd?: string, dryRun = false): boolean {
  if (dryRun) {
    logger.info(`[DRY RUN] ${command}`);
    return true;
  }

  try {
    execSync(command, { cwd, stdio: 'inherit' });
    return true;
  } catch {
    return false;
  }
}

/**
 * Runs a command, ALWAYS echoes its combined output to the console (so the user still
 * sees NuGet errors exactly as before), and returns the captured text so callers can
 * parse it — needed for NU1605 auto-repair, which `stdio:'inherit'` cannot support.
 */
function execCapture(command: string, cwd?: string): { ok: boolean; output: string } {
  try {
    const out = execSync(command, { cwd, encoding: 'utf-8', stdio: ['ignore', 'pipe', 'pipe'] });
    if (out) process.stdout.write(out);
    return { ok: true, output: out ?? '' };
  } catch (error) {
    const e = error as { stdout?: string | Buffer; stderr?: string | Buffer };
    const output = `${e.stdout?.toString() ?? ''}${e.stderr?.toString() ?? ''}`;
    if (output) process.stdout.write(output);
    return { ok: false, output };
  }
}

/**
 * After a failed `dotnet restore`, auto-aligns direct PackageReferences that NuGet
 * flagged as downgrades (NU1605) — the recurring failure when SmartStack raises a
 * transitive floor (e.g. EF Core 10.0.5 → 10.0.7) past a version the `.csproj` still
 * pins directly. Each conflicting direct reference is bumped UP to the required floor
 * (NuGet's own prescribed fix), then the solution is re-restored. A bounded loop
 * absorbs cascades where one bump surfaces the next conflict.
 *
 * Returns whether the restore ultimately succeeds, the alignments performed, and any
 * downgrades still unresolved (different/unfixable conflicts) for the remediation hint.
 */
async function repairNuGetDowngrades(
  firstFailureOutput: string,
  allProjects: ProjectUpgradeInfo[],
  projectDir: string,
  dryRun: boolean,
): Promise<{ resolved: boolean; aligned: string[]; unresolved: string[] }> {
  const aligned: string[] = [];
  let lastOutput = firstFailureOutput;
  const MAX_PASSES = 3;

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const downgrades = parseNuGetDowngrades(lastOutput);
    if (downgrades.length === 0) break; // nothing (more) we can auto-fix

    let bumpedAny = false;
    for (const d of downgrades) {
      const project = allProjects.find(
        (p) => basename(p.csprojPath).replace(/\.csproj$/i, '') === d.project,
      );
      if (!project) {
        logger.warning(`  ${chalk.yellow('?')} NU1605 on ${d.package} in unrecognised project ${d.project} — left for manual fix`);
        continue;
      }

      const cmd = `dotnet add "${project.csprojPath}" package ${d.package} --version ${d.requiredVersion} --no-restore`;
      if (dryRun) {
        logger.info(`  [DRY RUN] would align ${chalk.cyan(d.package)} → ${d.requiredVersion} in ${chalk.cyan(d.project)}`);
        aligned.push(`${d.project}: ${d.package} → ${d.requiredVersion}`);
        bumpedAny = true;
        continue;
      }

      if (execCapture(cmd).ok) {
        logger.success(`  ${chalk.green('↑')} Aligned ${chalk.cyan(d.package)} → ${chalk.cyan(d.requiredVersion)} in ${chalk.cyan(d.project)} (was downgrading a transitive dependency)`);
        aligned.push(`${d.project}: ${d.package} → ${d.requiredVersion}`);
        bumpedAny = true;
      } else {
        logger.error(`  ${chalk.red('✗')} Could not set ${d.package} ${d.requiredVersion} in ${d.project}`);
      }
    }

    if (!bumpedAny) break; // could not act on any downgrade — stop
    if (dryRun) return { resolved: false, aligned, unresolved: [] };

    logger.info('Re-restoring after alignment...');
    const restore = execCapture('dotnet restore', projectDir);
    if (restore.ok) return { resolved: true, aligned, unresolved: [] };
    lastOutput = restore.output;
  }

  const unresolved = [...new Set(parseNuGetDowngrades(lastOutput).map((d) => d.package))];
  return { resolved: false, aligned, unresolved };
}

/**
 * Validates that Program.cs contains all required SmartStack bootstrap calls.
 * Returns a list of missing calls (empty = all good).
 */
async function validateProgramCs(
  projectDir: string,
  baseNamespace: string,
): Promise<string[]> {
  const programPath = join(projectDir, 'src', `${baseNamespace}.Api`, 'Program.cs');

  if (!await fs.pathExists(programPath)) {
    return ['Program.cs not found'];
  }

  const content = await fs.readFile(programPath, 'utf-8');

  const requiredCalls = [
    { pattern: /\.AddSmartStack\s*\(/, label: 'AddSmartStack()' },
    { pattern: /\.InitializeSmartStackAsync\s*\(/, label: 'InitializeSmartStackAsync()' },
    { pattern: /\.UseSmartStack\s*\(/, label: 'UseSmartStack()' },
    { pattern: /\.MapSmartStack\s*\(/, label: 'MapSmartStack()' },
  ];

  const missing: string[] = [];
  for (const { pattern, label } of requiredCalls) {
    if (!pattern.test(content)) {
      missing.push(label);
    }
  }

  return missing;
}

/**
 * Writes a JSON reconciliation planned by the preflight. The plan was computed
 * from the file's content at preflight time; if the file changed since (nothing
 * in the upgrade writes it before this step), the plan is stale — skipped.
 */
async function applyJsonPlan(
  projectDir: string,
  plan: JsonSyncPlan,
  backup: BackupSet,
): Promise<boolean> {
  const abs = join(projectDir, ...plan.rel.split('/'));
  const current = (await fs.pathExists(abs)) ? await fs.readFile(abs, 'utf-8') : '';
  if (current !== plan.before) {
    logger.warning(`${plan.rel} changed during the upgrade — its reconciliation was skipped, re-run ss upgrade.`);
    return false;
  }
  await backup.write(plan.rel, plan.after);
  return true;
}

/**
 * Registers the project's client extension entities for the platform global search by invoking the
 * deterministic `scaffold-extension-search` discovery CLI. Idempotent and FAIL-SOFT — any problem here
 * is logged and skipped, it never breaks the upgrade. Row-scope-suspect entities are NOT auto-registered;
 * they are reported for manual review so search can never reveal rows their list hides.
 */
async function syncExtensionSearchRegistration(
  projectDir: string,
  appCode: string,
  dryRun: boolean,
): Promise<void> {
  // Reuse the proven TEMPLATES_DIR base; the skills tree is a sibling of templates/project.
  const cli = join(dirname(TEMPLATES_DIR), 'skills', 'development', 'backend', 'data-layer', 'cli', 'scaffold-extension-search', 'index.ts');
  if (!await fs.pathExists(cli)) {
    logger.debug('scaffold-extension-search CLI not found, skipping global-search registration');
    return;
  }

  // Only relevant for projects that actually declare client extension entities.
  const ctxFiles = await glob('**/ExtensionsDbContext.cs', {
    cwd: join(projectDir, 'src'),
    ignore: ['**/bin/**', '**/obj/**'],
  });
  if (ctxFiles.length === 0) {
    logger.debug('No ExtensionsDbContext found, skipping global-search registration');
    return;
  }

  logger.info('Registering client entities for global search...');
  try {
    const flags = `--discover --project "${projectDir}" --app "${appCode}"${dryRun ? ' --dry-run' : ''}`;
    const out = execSync(`npx --prefer-offline tsx "${cli}" ${flags}`, {
      cwd: dirname(dirname(TEMPLATES_DIR)),
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    const env = JSON.parse(out) as {
      data?: { registered?: string[]; review?: Array<{ kind: string; entity?: string; detail: string }> };
    };
    const registered = env.data?.registered ?? [];
    const review = env.data?.review ?? [];
    const suspects = review.filter((r) => r.kind === 'row-scope-suspect');

    if (registered.length > 0) {
      logger.success(`${dryRun ? '[DRY RUN] Would register' : 'Registered'} ${registered.length} search categor${registered.length === 1 ? 'y' : 'ies'}: ${registered.join(', ')}`);
    } else {
      logger.info(`Global search ${chalk.green('✓')} (nothing new to auto-register)`);
    }
    for (const s of suspects) {
      logger.warning(`[REVIEW] "${s.entity}" not auto-registered (row-scope) — add .RestrictTo(...) by hand. ${s.detail}`);
    }
    const unbound = review.filter((r) => r.kind !== 'row-scope-suspect');
    if (unbound.length > 0) {
      logger.info(`  ${unbound.length} entit${unbound.length === 1 ? 'y' : 'ies'} could not be auto-bound — see references/global-search.md.`);
    }
  } catch (err) {
    logger.warning(`Global-search registration skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Back-fills the HR time-entry imputation seam by invoking the deterministic `scaffold-time-entry-refs`
 * discovery CLI. Idempotent and FAIL-SOFT — any problem here is logged and skipped, it never breaks the
 * upgrade. It NEVER auto-registers a dimension (imputation is an opt-in business decision): it only ensures
 * the COMMENTED seam wrapper exists (so an older project learns the seam) and reports the extension entities
 * that are candidate dimensions for a human to promote.
 */
async function syncTimeEntryRefsRegistration(
  projectDir: string,
  appCode: string,
  dryRun: boolean,
): Promise<void> {
  // Reuse the proven TEMPLATES_DIR base; the skills tree is a sibling of templates/project.
  const cli = join(dirname(TEMPLATES_DIR), 'skills', 'development', 'backend', 'data-layer', 'cli', 'scaffold-time-entry-refs', 'index.ts');
  if (!await fs.pathExists(cli)) {
    logger.debug('scaffold-time-entry-refs CLI not found, skipping time-entry-refs seam back-fill');
    return;
  }

  // Only relevant for projects that actually declare client extension entities.
  const ctxFiles = await glob('**/ExtensionsDbContext.cs', {
    cwd: join(projectDir, 'src'),
    ignore: ['**/bin/**', '**/obj/**'],
  });
  if (ctxFiles.length === 0) {
    logger.debug('No ExtensionsDbContext found, skipping time-entry-refs seam back-fill');
    return;
  }

  logger.info('Back-filling the HR time-entry imputation seam...');
  try {
    const flags = `--discover --project "${projectDir}" --app "${appCode}"${dryRun ? ' --dry-run' : ''}`;
    const out = execSync(`npx --prefer-offline tsx "${cli}" ${flags}`, {
      cwd: dirname(dirname(TEMPLATES_DIR)),
      encoding: 'utf-8',
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    const env = JSON.parse(out) as {
      data?: {
        seamPresent?: boolean;
        seamBackfilled?: boolean;
        candidates?: string[];
        review?: Array<{ kind: string; entity?: string; detail: string }>;
      };
    };
    const candidates = env.data?.candidates ?? [];

    if (env.data?.seamBackfilled) {
      logger.success(`${dryRun ? '[DRY RUN] Would back-fill' : 'Back-filled'} the commented time-entry-refs seam (nothing registered — imputation is opt-in)`);
    } else if (env.data?.seamPresent) {
      logger.info(`Time-entry-refs seam ${chalk.green('✓')} (already present)`);
    } else {
      logger.info(`Time-entry-refs seam ${chalk.green('✓')} (nothing to back-fill)`);
    }
    if (candidates.length > 0) {
      logger.info(`  ${candidates.length} extension entit${candidates.length === 1 ? 'y is a' : 'ies are'} candidate imputation dimension${candidates.length === 1 ? '' : 's'} — register the ones time is booked against by hand. See references/time-entry-refs.md.`);
    }
  } catch (err) {
    logger.warning(`Time-entry-refs seam back-fill skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Back-fills the export-datasets seam into an existing project's DependencyInjection.cs.
 *
 * Unlike global search and time-entry refs, this seam has NO scaffolder — a dataset's fields, its
 * measures and its pivots are business decisions a column-mapping DSL cannot express — so there is
 * nothing to discover and nothing to auto-register. All this does is ensure the COMMENTED marker
 * block exists, so a project generated before the seam learns it is there.
 *
 * Idempotent and FAIL-SOFT: any problem is logged and skipped, it never breaks the upgrade.
 *
 * It also surfaces the v3.66 BEHAVIOURAL change for projects that already ship a provider — an
 * emptied filter used to fall back to the provider's defaults and now means "nothing selected". No
 * `ss upgrade` can rewrite a provider's business logic, so the only honest thing is to say so.
 */
async function syncExportDatasetsRegistration(
  projectDir: string,
  baseNamespace: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<void> {
  const diPath = join(projectDir, 'src', `${baseNamespace}.Infrastructure`, 'DependencyInjection.cs');
  if (!await fs.pathExists(diPath)) {
    logger.debug('DependencyInjection.cs not found, skipping export-datasets seam back-fill');
    return;
  }

  try {
    const content = await fs.readFile(diPath, 'utf-8');
    const patched = insertExportDatasetsSeam(content, baseNamespace);

    if (content.includes(EXPORT_DATASETS_BEGIN)) {
      logger.info(`Export-datasets seam ${chalk.green('✓')} (already present)`);
    } else if (patched === null) {
      logger.info(
        `Export-datasets seam not back-filled — add the ${chalk.cyan('<<< EXPORT-DATASETS-DI >>>')} ` +
        'markers by hand. See references/export-datasets.md.',
      );
    } else {
      if (!dryRun) await backup.write(diPath, patched);
      logger.success(
        `${dryRun ? '[DRY RUN] Would back-fill' : 'Back-filled'} the commented export-datasets seam ` +
        '(nothing registered — an exportable dataset is an opt-in business decision)',
      );
    }

    // A project that already registers a provider has business logic this upgrade cannot fix.
    if (content.includes('AddSmartStackExportDatasetProvider')) {
      logger.warning(
        '[REVIEW] Export datasets: an emptied filter now returns an EMPTY collection from ' +
        'ctx.Filter(...) instead of falling back to your DefaultValues. Return ' +
        'ExportDatasetResult.Empty on it, or unticking every option in the wizard will export ' +
        'everything. Stored templates are migrated on read. See references/export-datasets.md.',
      );
    }
  } catch (err) {
    logger.warning(`Export-datasets seam back-fill skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/** What GitFlow believes the version is (`.gitflow/config.json`, in the project or its worktree root). */
async function readGitflowCurrentVersion(projectDir: string): Promise<string | null> {
  for (const dir of [projectDir, dirname(projectDir)]) {
    const configPath = join(dir, '.gitflow', 'config.json');
    if (!await fs.pathExists(configPath)) continue;
    try {
      const config = await fs.readJson(configPath) as { versioning?: { current?: unknown } };
      const current = config.versioning?.current;
      return typeof current === 'string' ? current : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Latest release tag reachable from HEAD, or null (no repo, no tag). Read-only. */
function readLatestTag(projectDir: string): string | null {
  try {
    return execSync('git describe --tags --abbrev=0', {
      cwd: projectDir, encoding: 'utf-8', timeout: 5000, stdio: ['ignore', 'pipe', 'ignore'],
    }).trim() || null;
  } catch {
    return null;
  }
}

/**
 * Gives an existing client project a PRODUCT version the running API can report (the platform's
 * user menu: "Atlas Hub 1.4.2 · SmartStack 3.70.0"): a root package.json — the file GitFlow bumps —
 * read by Directory.Build.props into <VersionPrefix>. See lib/client-version.ts.
 *
 * Never overwrites: an existing version, an existing <VersionPrefix> or a literal <Version> the
 * client pinned are all left alone. Runs BEFORE the docker sync, whose Dockerfile copies the
 * package.json into the build stage. Every write goes through the upgrade backup (--restore).
 */
async function syncClientVersioning(
  projectDir: string,
  nameLower: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<string[]> {
  const changes: string[] = [];
  const written: Array<{ relPath: string; content: string }> = [];
  const seed = resolveSeedVersion({
    gitflowCurrent: await readGitflowCurrentVersion(projectDir),
    latestTag: readLatestTag(projectDir),
  });

  const pkgPath = join(projectDir, 'package.json');
  if (!await fs.pathExists(pkgPath)) {
    const content = buildRootPackageJson(nameLower, seed);
    written.push({ relPath: 'package.json', content });
    changes.push(`package.json (version ${seed})`);
  } else {
    const updated = ensureVersionField(await fs.readFile(pkgPath, 'utf-8'), seed);
    if (updated) {
      written.push({ relPath: 'package.json', content: updated });
      changes.push(`package.json: "version": "${seed}"`);
    }
  }

  const propsPath = join(projectDir, 'Directory.Build.props');
  if (await fs.pathExists(propsPath)) {
    const { content, status } = insertVersionPrefix(await fs.readFile(propsPath, 'utf-8'));
    if (status === 'inserted') {
      written.push({ relPath: 'Directory.Build.props', content });
      changes.push('Directory.Build.props: <VersionPrefix> reads the root package.json');
    } else if (status === 'literal-version') {
      logger.warning('Directory.Build.props pins a literal <Version> — left as is; GitFlow will not bump it.');
    } else if (status === 'no-property-group') {
      logger.warning('Directory.Build.props has no <PropertyGroup> — product version not wired.');
    }
  }

  if (dryRun || written.length === 0) return changes;

  for (const file of written) {
    await backup.write(file.relPath, file.content);
  }
  // Keep these files "pristine" for a later in-place `ss init`: record the hashes we wrote.
  const state = await loadInitState(projectDir);
  if (state) {
    for (const file of written) recordFile(state, 'config', file.relPath, computeHash(file.content));
    state.updatedAt = new Date().toISOString();
    await saveInitState(projectDir, state);
  }
  return changes;
}

/**
 * Ensures `scripts/patch-smartstack-theme.cjs` exists in the frontend project and
 * that the upstream theme.css bug is patched right now (if node_modules is already
 * populated). Returns the number of files written (0 or 1).
 *
 * The package.json `postinstall` hook is added by the matching CodeMigration
 * (smartstack-theme-postinstall-migration); this function only handles the
 * provisioning of the script file itself and the immediate in-place patch.
 */
async function syncFrontendPostinstallScript(
  frontendDir: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<number> {
  const scriptDest = join(frontendDir, 'scripts', 'patch-smartstack-theme.cjs');
  const scriptTemplate = join(TEMPLATES_DIR, 'patch-smartstack-theme.cjs.template');

  if (!await fs.pathExists(scriptTemplate)) {
    logger.debug('patch-smartstack-theme.cjs.template not found, skipping');
    return 0;
  }

  let written = 0;
  if (!await fs.pathExists(scriptDest)) {
    if (dryRun) {
      logger.warning('[DRY RUN] Would create scripts/patch-smartstack-theme.cjs');
    } else {
      const content = await fs.readFile(scriptTemplate, 'utf-8');
      await backup.write(scriptDest, content);
      logger.info(`  ${chalk.green('+')} scripts/patch-smartstack-theme.cjs`);
      written = 1;
    }
  }

  // Apply the patch immediately if node_modules is already installed (build
  // output of npm — not part of the upgrade backup).
  const themePath = join(
    frontendDir,
    'node_modules',
    '@atlashub',
    'smartstack',
    'dist',
    'theme.css',
  );
  if (!dryRun && await fs.pathExists(themePath)) {
    const original = await fs.readFile(themePath, 'utf-8');
    const patched = original.replace(/\*\*\/\*\.js/g, '** /*.js');
    if (patched !== original) {
      await fs.writeFile(themePath, patched, 'utf-8');
      logger.info(`  ${chalk.green('✓')} Patched node_modules/@atlashub/smartstack/dist/theme.css`);
    }
  }

  return written;
}

async function syncTestFrontendTemplates(
  frontendDir: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<number> {
  const testFrontendDir = join(TEMPLATES_DIR, 'test-frontend');
  if (!await fs.pathExists(testFrontendDir)) {
    logger.debug('test-frontend templates not found, skipping');
    return 0;
  }

  let synced = 0;

  const testFiles = [
    { src: 'vitest.config.ts', dest: 'vitest.config.ts' },
    { src: 'setup.ts', dest: join('src', 'test', 'setup.ts') },
    { src: 'test-utils.tsx', dest: join('src', 'test', 'test-utils.tsx') },
    { src: 'msw/handlers.ts', dest: join('src', 'test', 'msw', 'handlers.ts') },
    { src: 'msw/server.ts', dest: join('src', 'test', 'msw', 'server.ts') },
  ];

  for (const file of testFiles) {
    const destPath = join(frontendDir, file.dest);
    if (await fs.pathExists(destPath)) continue;

    const srcPath = join(testFrontendDir, file.src);
    if (!await fs.pathExists(srcPath)) continue;

    if (dryRun) {
      logger.warning(`[DRY RUN] Would create ${file.dest}`);
    } else {
      const content = await fs.readFile(srcPath, 'utf-8');
      await backup.write(destPath, content);
      logger.info(`  ${chalk.green('+')} ${file.dest}`);
    }
    synced++;
  }

  return synced;
}

/** Deep-merge two parsed JSON trees, the EXISTING (target) side winning on
 *  every leaf conflict — hand-edited copy in the real locales tree is never
 *  overwritten by a recovered phantom bundle. */
function mergePreferExisting(incoming: unknown, existing: unknown): unknown {
  if (
    incoming !== null && existing !== null &&
    typeof incoming === 'object' && typeof existing === 'object' &&
    !Array.isArray(incoming) && !Array.isArray(existing)
  ) {
    const inc = incoming as Record<string, unknown>;
    const out: Record<string, unknown> = { ...inc };
    for (const [key, value] of Object.entries(existing as Record<string, unknown>)) {
      out[key] = key in inc ? mergePreferExisting(inc[key], value) : value;
    }
    return out;
  }
  return existing;
}

async function removeDirIfEmpty(dir: string): Promise<void> {
  try {
    const entries = await fs.readdir(dir);
    if (entries.length === 0) await fs.remove(dir);
  } catch {
    // missing or unreadable — leave it alone
  }
}

/**
 * Repairs the pre-5.11 scaffold-component bug that wrote module locale JSON to
 * `<frontendDir>/web/<app>-web/src/i18n/locales/…` — a phantom double-nested
 * tree the runtime never loads (frontendDir already IS the web root), so
 * business pages rendered raw i18n keys. Deep-merges every phantom bundle into
 * the real `src/i18n/locales/<locale>/` (existing target wins on leaf
 * conflicts) and prunes the phantom tree. Fail-soft: any error logs a warning
 * and returns what was already done. Every file is captured in the upgrade
 * backup before it is merged, copied or pruned.
 */
async function relocatePhantomI18nLocales(
  frontendDir: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<number> {
  let relocated = 0;
  try {
    const phantomRoots = await glob('web/*-web/src/i18n/locales', { cwd: frontendDir, absolute: true });
    for (const phantomRoot of phantomRoots) {
      if (!dryRun) {
        await backup.capture(await glob('**/*', { cwd: phantomRoot, absolute: true, nodir: true }));
      }
      const locales = (await fs.readdir(phantomRoot, { withFileTypes: true }))
        .filter((d) => d.isDirectory())
        .map((d) => d.name);
      for (const locale of locales) {
        const localeDir = join(phantomRoot, locale);
        const bundles = (await fs.readdir(localeDir)).filter((f) => f.endsWith('.json'));
        for (const bundle of bundles) {
          const relDest = join('src', 'i18n', 'locales', locale, bundle);
          if (dryRun) {
            logger.warning(`[DRY RUN] Would recover phantom i18n bundle → ${relDest}`);
            relocated++;
            continue;
          }
          const src = join(localeDir, bundle);
          const dest = join(frontendDir, relDest);
          if (await fs.pathExists(dest)) {
            try {
              const incoming = JSON.parse(await fs.readFile(src, 'utf-8')) as unknown;
              const existing = JSON.parse(await fs.readFile(dest, 'utf-8')) as unknown;
              const merged = mergePreferExisting(incoming, existing);
              await backup.write(dest, JSON.stringify(merged, null, 2) + '\n');
            } catch {
              // malformed JSON on either side — keep the existing target untouched
              continue;
            }
          } else {
            await backup.write(dest, await fs.readFile(src));
          }
          logger.info(`  ${chalk.green('↻')} ${relDest} (recovered from phantom web/ tree)`);
          relocated++;
        }
      }
      if (!dryRun) {
        await fs.remove(phantomRoot); // …/web/<x>-web/src/i18n/locales
        const i18nDir = dirname(phantomRoot);
        await removeDirIfEmpty(i18nDir); // …/src/i18n
        await removeDirIfEmpty(dirname(i18nDir)); // …/src
        await removeDirIfEmpty(dirname(dirname(i18nDir))); // …/web/<x>-web
        await removeDirIfEmpty(join(frontendDir, 'web')); // …/web
      }
    }
  } catch (error) {
    logger.warning(`Phantom i18n relocation skipped: ${error instanceof Error ? error.message : String(error)}`);
  }
  return relocated;
}

/**
 * Removes the platform-admin fallback block from `componentRegistry.generated.ts`, then deletes
 * the placeholder component it leaves orphaned.
 *
 * A project generated by CLI ≤ 5.12 would keep BLANK `/administration/*` landing pages until
 * someone happened to run /scaffold-routes. This is text surgery on a file the generator fully
 * owns — anchored on the emitted header, no brace scan, idempotent. (The project-wide regen at
 * the end of the upgrade then re-aggregates the registry from the per-module files.)
 *
 * Order matters: strip FIRST, classify second. Deleting the component while the registry still
 * imports it would turn a working page into a Vite resolution failure.
 *
 * Fail-soft, like its siblings: any error logs a warning and returns what was already done.
 */
async function syncPlatformAdminFallbacks(
  frontendDir: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<number> {
  let changed = 0;
  try {
    const registryPath = join(frontendDir, 'src', 'extensions', 'componentRegistry.generated.ts');
    if (await fs.pathExists(registryPath)) {
      const content = await fs.readFile(registryPath, 'utf-8');
      const stripped = stripPlatformAdminFallbacks(content);
      if (stripped !== null) {
        if (!dryRun) await backup.write(registryPath, stripped);
        logger.success(
          `${dryRun ? '[DRY RUN] Would remove' : 'Removed'} the platform-admin fallback block from ` +
          `componentRegistry.generated.ts — /administration and every /administration/<module> root ` +
          `now render the SDK's own landing page instead of a blank placeholder`
        );
        changed++;
      }
    }

    const componentPath = join(frontendDir, 'src', 'components', 'AdminModuleFallback.tsx');
    if (await fs.pathExists(componentPath)) {
      const source = await fs.readFile(componentPath, 'utf-8');
      const others = await glob('src/**/*.{ts,tsx}', {
        cwd: frontendDir, absolute: true, ignore: MIGRATION_IGNORE_GLOBS,
      });
      const otherSources = await Promise.all(
        others.filter((f) => f !== componentPath).map((f) => fs.readFile(f, 'utf-8')),
      );
      switch (classifyAdminModuleFallback(source, otherSources)) {
        case 'orphan':
          if (!dryRun) await backup.remove(componentPath);
          logger.info(`  ${chalk.red('−')} ${dryRun ? '[DRY RUN] ' : ''}Removed orphan src/components/AdminModuleFallback.tsx`);
          changed++;
          break;
        case 'customised':
          logger.warning(
            'src/components/AdminModuleFallback.tsx is marked @customised and was KEPT, but nothing ' +
            `imports it anymore (${ADMIN_FALLBACK_SPECIFIER}). Delete it, or register it on a ` +
            'componentKey of your own.'
          );
          break;
        case 'still-referenced':
          logger.debug('AdminModuleFallback.tsx still referenced — kept.');
          break;
      }
    }
  } catch (error) {
    logger.warning(`Platform-admin fallback cleanup skipped: ${error instanceof Error ? error.message : String(error)}`);
  }
  return changed;
}

async function cleanResidualFiles(
  projectDir: string,
  baseNamespace: string,
  dryRun: boolean,
  backup: BackupSet,
): Promise<number> {
  let cleaned = 0;

  const residuals = [
    join(projectDir, 'src', `${baseNamespace}.Api`, `${baseNamespace}.Api.http`),
  ];

  for (const file of residuals) {
    if (await fs.pathExists(file)) {
      if (dryRun) {
        logger.warning(`[DRY RUN] Would remove residual: ${file}`);
      } else {
        await backup.remove(file);
        logger.info(`  ${chalk.red('−')} Removed residual: ${baseNamespace}.Api.http`);
      }
      cleaned++;
    }
  }

  return cleaned;
}

/**
 * init-state.json carried `cliVersion: "0.0.0"` from a pre-fix init. Repair it with
 * the RUNNING CLI version — never the NuGet version, which an earlier upgrade wrote
 * there by mistake (the field names the CLI that generated the project).
 */
async function fixInitStateVersion(
  projectDir: string,
  cliVersion: string,
  dryRun: boolean,
): Promise<boolean> {
  const statePath = join(projectDir, '.smartstack', 'init-state.json');
  if (!await fs.pathExists(statePath)) return false;

  try {
    const state = await fs.readJson(statePath);
    if (state.cliVersion === '0.0.0') {
      if (dryRun) {
        logger.warning('[DRY RUN] Would fix cliVersion 0.0.0 in init-state.json');
      } else {
        state.cliVersion = cliVersion;
        await fs.writeJson(statePath, state, { spaces: 2 });
        logger.info(`  ${chalk.green('+')} Fixed cliVersion: 0.0.0 → ${cliVersion}`);
      }
      return true;
    }
  } catch {
    // Non-critical
  }
  return false;
}

/** Final-summary lines for the DataProtection findings of the upgrade. */
function keyRingSummaryLines(result: UpgradeResult): string[] {
  const lines: string[] = [];
  if (result.keyRingOptOuts.length > 0) {
    lines.push(`  ${chalk.red('✗')} DataProtection: RequireEncryptedKeyRing=false in ${result.keyRingOptOuts.join(', ')} — remove it`);
  }
  if (result.dockerComposeLacksCertificate) {
    lines.push(`  ${chalk.yellow('⚠')} DataProtection: docker-compose.yml maps no certificate — add the lines printed above`);
  }
  return lines;
}

/**
 * Ensures all required NuGet packages are present in each project.
 * Adds missing packages without removing existing ones.
 * Handles projects created by older CLI versions that may be missing
 * packages added in later releases (e.g., SignalR.StackExchangeRedis).
 */
async function ensureRequiredPackages(
  projects: ProjectUpgradeInfo[],
  baseNamespace: string,
  dryRun: boolean,
): Promise<{ added: string[]; failed: string[] }> {
  const added: string[] = [];
  const failed: string[] = [];

  for (const rp of REQUIRED_PACKAGES) {
    if (rp.packages.length === 0) continue;

    // Find the project matching this suffix (e.g., "MyApp.Api")
    const expectedFolder = `${baseNamespace}.${rp.suffix}`;
    const project = projects.find(p => {
      const folderName = p.relPath.split(/[/\\]/).slice(-2, -1)[0];
      return folderName === expectedFolder;
    });

    if (!project) continue;

    const currentPackages = readAllPackageReferences(project.csprojContent);
    const currentNames = new Set(currentPackages.map(p => p.name.toLowerCase()));

    for (const requiredPkg of rp.packages) {
      if (currentNames.has(requiredPkg.toLowerCase())) continue;

      // Package is missing — add it. For locked families (e.g. Hangfire.*) pin the
      // version to the family member already in the .csproj; otherwise `dotnet add`
      // resolves the floating latest whose exact core dependency outranks the existing
      // direct reference → NU1605 (package downgrade) → broken restore.
      const pin = resolveLockedFamilyVersion(requiredPkg, currentPackages);
      const versionFlag = pin ? ` --version ${pin}` : '';
      const pinNote = pin
        ? ` ${chalk.gray(`(pinned to ${pin} to match existing ${requiredPkg.split('.')[0]}.* reference)`)}`
        : '';
      try {
        if (dryRun) {
          logger.info(`  [DRY RUN] Would add ${chalk.cyan(requiredPkg)}${pin ? `@${pin}` : ''} to ${chalk.cyan(expectedFolder)}`);
        } else {
          execCommand(
            `dotnet add "${project.csprojPath}" package ${requiredPkg}${versionFlag} --no-restore`,
            undefined,
            false,
          );
          logger.success(`  ${chalk.green('+')} Added ${chalk.cyan(requiredPkg)} to ${chalk.cyan(expectedFolder)}${pinNote}`);
        }
        added.push(`${expectedFolder} + ${requiredPkg}`);
      } catch {
        failed.push(`${expectedFolder} + ${requiredPkg}`);
        logger.error(`  ${chalk.red('✗')} Failed to add ${requiredPkg} to ${expectedFolder}`);
      }
    }
  }

  return { added, failed };
}

/** Log the outcome of a JSON reconciliation (appsettings.json / .claude/settings.json). */
function logJsonSync(label: string, plan: JsonSyncPlan | null, dryRun: boolean): number {
  if (!plan) {
    logger.info(`${label} ${chalk.green('✓')} up to date`);
    console.log();
    return 0;
  }
  const total = plan.added.length + plan.removed.length + plan.relocated.length + plan.corrected.length;
  const suffix = (plan.relocated.length > 0 ? ` / ↻${plan.relocated.length}` : '') + (plan.corrected.length > 0 ? ` / ~${plan.corrected.length}` : '');
  if (dryRun) logger.warning(`[DRY RUN] Would reconcile ${label} (+${plan.added.length} / -${plan.removed.length}${suffix})`);
  for (const key of plan.relocated) logger.info(`  ${chalk.cyan('↻')} ${key} (relocated to its new section)`);
  for (const key of plan.added) logger.info(`  ${chalk.green('+')} ${key}`);
  for (const key of plan.removed) logger.info(`  ${chalk.red('-')} ${key}`);
  for (const line of plan.corrected) logger.info(`  ${chalk.yellow('~')} ${line} (value shipped wrong by an earlier template)`);
  if (!dryRun) logger.success(`${label} reconciled (+${plan.added.length} / -${plan.removed.length}${suffix})`);
  console.log();
  return total;
}

function printPreflightErrors(plan: UpgradePlan): void {
  logger.error(`Preflight failed — NOTHING was written (${plan.errors.length} problem(s)):`);
  for (const e of plan.errors) console.log(`  ${chalk.red('✗')} ${e.message}`);
  console.log();
}

function printPlanSummary(plan: UpgradePlan): void {
  const { from, to } = plan.versions;
  logger.info(`Project: ${chalk.cyan(plan.baseNamespace || '?')} (${plan.projectType})`);
  if (plan.git?.isRepo) logger.info(`Branch: ${chalk.cyan(plan.git.branch ?? 'detached HEAD')}`);
  logger.info(`SmartStack (NuGet): ${chalk.yellow(from.socle ?? 'unknown')} → ${chalk.cyan(to.socle ?? '?')}${plan.socleUpToDate ? chalk.green(' (up to date)') : ''}`);
  if (plan.frontendDir) {
    logger.info(`${SOCLE_NPM_NAME} (npm): ${chalk.yellow(from.npm ?? 'unknown')} → ${chalk.cyan(to.npm ?? '?')}${plan.npmUpToDate ? chalk.green(' (up to date)') : ''}`);
  }
  logger.info(`Generator (CLI): ${chalk.yellow(from.cli ?? 'unknown')} → ${chalk.cyan(to.cli ?? '?')}`);
  if (plan.packageEdits.length > 0) {
    logger.info(`Package bumps planned (${plan.packageEdits.length}):`);
    for (const e of plan.packageEdits) logger.info(`  ${chalk.yellow('↑')} ${e.rel}: ${e.id} ${e.from} → ${e.to}`);
  }
  if (plan.codemods.files.length > 0) {
    logger.info(`Code migrations planned on ${plan.codemods.files.length} file(s):`);
    for (const f of plan.codemods.files) logger.info(`  • ${f.rel} — ${f.descriptions.join('; ')}`);
  }
  for (const w of plan.warnings) logger.warning(w);
  for (const s of plan.suggestions) logger.info(chalk.gray(`Tip: ${s}`));
  console.log();
}

async function listBackups(projectDir: string): Promise<UpgradeRunResult> {
  const backups = await BackupSet.list(projectDir);
  if (backups.length === 0) {
    logger.info('No upgrade backup in .smartstack/upgrade-backups/.');
    return { status: 'listed', exitCode: 0 };
  }
  logger.info(`${backups.length} upgrade backup(s), newest first:`);
  for (const b of backups) {
    const state = b.restoredAt ? chalk.gray(`restored ${b.restoredAt}`) : b.finalizedAt ? chalk.green('complete') : chalk.yellow('unfinished');
    console.log(`  ${chalk.cyan(b.id)}  SmartStack ${b.from.socle ?? '?'} → ${b.to.socle ?? '?'}  ${b.files.length} file(s)  ${b.branch ?? ''}  ${state}`);
  }
  console.log();
  logger.info(`Restore one with ${chalk.cyan('ss upgrade --restore <id>')} (or ${chalk.cyan('--restore latest')}).`);
  return { status: 'listed', exitCode: 0 };
}

function printRestoreReport(report: RestoreReport): void {
  logger.success(`Backup ${chalk.cyan(report.id)} restored: ${report.restored.length} file(s) put back, ${report.deleted.length} created file(s) removed, ${report.unchanged.length} already in place.`);
  for (const rel of report.restored) console.log(`  ${chalk.green('↺')} ${rel}`);
  for (const rel of report.deleted) console.log(`  ${chalk.red('−')} ${rel}`);
  if (report.conflicts.length > 0) {
    logger.warning(`${report.conflicts.length} file(s) NOT restored (changed after the upgrade):`);
    for (const c of report.conflicts) {
      console.log(`  ${chalk.yellow('!')} ${c.rel} — ${c.reason}${c.backupCopy ? `\n      pre-upgrade copy: ${c.backupCopy}` : ''}`);
    }
  }
  console.log();
  logger.info(`Review with ${chalk.cyan('git status')} / ${chalk.cyan('git diff')}. NuGet/npm caches are not touched: run ${chalk.cyan('dotnet restore')} and ${chalk.cyan('npm install')} in the web app.`);
}

async function restoreBackup(projectDir: string, id: string): Promise<UpgradeRunResult> {
  try {
    const report = await BackupSet.restore(projectDir, id);
    printRestoreReport(report);
    return { status: 'restored', exitCode: report.conflicts.length > 0 ? 1 : 0, backupId: report.id };
  } catch (err) {
    logger.error(err instanceof Error ? err.message : String(err));
    return { status: 'aborted', exitCode: 1 };
  }
}

/** Files the upgrade's external writers (dotnet, npm, docker sync, …) may touch — captured up front. */
async function externalWriteTargets(projectDir: string, plan: UpgradePlan): Promise<string[]> {
  const ignore = [...MIGRATION_IGNORE_GLOBS, '**/.smartstack/upgrade-backups/**'];
  const targets = await glob(['**/*.csproj', 'Directory.Packages.props', 'Directory.Build.props', 'src/**/DependencyInjection*.cs', '.ralph/**/*'], {
    cwd: projectDir, absolute: true, nodir: true, ignore, dot: true,
  });
  for (const spec of DOCKER_SYNC_FILES) targets.push(join(projectDir, spec.relPath), join(projectDir, `${spec.relPath}.bak`));
  targets.push(
    join(projectDir, ...DOCKER_ENV_REL_PATH.split('/')),
    join(projectDir, '.gitignore'),
    join(projectDir, '.smartstack', 'init-state.json'),
  );
  if (plan.frontendDir) {
    targets.push(
      join(plan.frontendDir, 'package.json'),
      join(plan.frontendDir, 'package-lock.json'),
      join(plan.frontendDir, 'src', 'extensions', 'componentRegistry.generated.ts'),
      join(plan.frontendDir, 'src', 'components', 'AdminModuleFallback.tsx'),
    );
  }
  return targets;
}

/**
 * `ss upgrade` — PREFLIGHT (no write) → APPLY (every write through the backup)
 * → REGEN → BOOKKEEPING → SKILLS → VERIFY → report. Exported for tests: the
 * preflight refusals are asserted to leave the project byte-identical.
 */
export async function runUpgrade(options: UpgradeOptions, deps: UpgradeDeps = {}): Promise<UpgradeRunResult> {
  const projectDir = deps.projectDir ?? process.cwd();
  const cliVersion = getCliVersion();
  logger.header('SmartStack Project Upgrade');

  // ── Maintenance modes ────────────────────────────────────────────────────
  if (options.listBackups) return listBackups(projectDir);
  if (options.restore !== undefined && options.restore !== false) {
    return restoreBackup(projectDir, options.restore === true ? 'latest' : String(options.restore));
  }

  const dryRun = options.dryRun || false;
  if (dryRun) {
    logger.warning('DRY RUN MODE - nothing will be written');
    console.log();
  }

  // ── 1. PREFLIGHT — no write ──────────────────────────────────────────────
  logger.info('Preflight: git state, configuration files, versions...');
  const plan = await runUpgradePreflight({
    projectDir,
    cliVersion,
    templatesDir: TEMPLATES_DIR,
    registry: deps.registry ?? defaultRegistry,
    io: { isTTY: deps.isTTY ?? Boolean(process.stdin.isTTY && process.stdout.isTTY), confirm: deps.confirm ?? defaultConfirm },
    options: {
      allowDirty: options.allowDirty,
      allowProtected: options.allowProtectedBranch,
      version: options.version,
      allowDowngrade: options.allowDowngrade,
      preview: options.preview,
      allowCommentLoss: options.allowCommentLoss,
      dryRun,
    },
  });
  console.log();
  if (plan.baseNamespace) printPlanSummary(plan);
  if (!plan.ok) {
    printPreflightErrors(plan);
    process.exitCode = 1;
    return { status: 'aborted', exitCode: 1, plan };
  }

  const startedAt = new Date().toISOString();
  const baseNamespace = plan.baseNamespace;
  const socleTo = plan.versions.to.socle!;
  const npmTo = plan.versions.to.npm;
  const frontendDir = plan.frontendDir;
  let config: ProjectConfig = plan.config.value;

  const backup = BackupSet.open(projectDir, {
    from: plan.versions.from,
    to: plan.versions.to,
    gitHead: plan.git?.head ?? null,
    branch: plan.git?.branch ?? null,
  }, { dryRun });

  const writeConfig = async (next: ProjectConfig): Promise<void> => {
    config = next;
    if (!dryRun && plan.config.exists) await backup.write(PROJECT_CONFIG_REL, serializeProjectConfig(config));
  };

  const result: UpgradeResult = {
    nugetUpgraded: 0,
    nugetSkipped: 0,
    nugetFailed: 0,
    otherPkgUpdated: 0,
    otherPkgSkipped: 0,
    otherPkgFailed: 0,
    npmUpgraded: false,
    npmSkipped: false,
    npmFailed: false,
    npmOtherUpdated: false,
    configSynced: 0,
    claudeSettingsSynced: 0,
    ralphPurged: false,
    dockerSynced: 0,
    dockerSkippedModified: [],
    dockerSecretsProvisioned: [],
    dockerComposeLacksCertificate: false,
    keyRingOptOuts: [],
    testFrontendSynced: 0,
    i18nBundlesRelocated: 0,
    adminFallbacksCleaned: 0,
    residualsCleaned: 0,
    initStateFixed: false,
    requiredPkgAdded: 0,
    requiredPkgFailed: 0,
    restoreFailed: false,
    packagesAligned: 0,
    unresolvedDowngrades: [],
    programCsIssues: [],
    codemodsApplied: 0,
    verifyFailed: false,
  };
  const reportErrors: string[] = [];
  const reportWarnings: string[] = [...plan.warnings];

  // ── 2. APPLY — every write goes through the backup ───────────────────────
  if (!dryRun) {
    // Crash marker first: an upgrade that dies half-way stays visible (ss doctor, LIFE-013).
    await writeConfig(withLastUpgrade(config, {
      cliVersion,
      socleVersion: plan.versions.from.socle,
      npmVersion: plan.versions.from.npm,
      at: startedAt,
      status: 'in-progress',
      backupId: backup.id,
      // A crash replays the cli codemods from HERE (project-versions projectCliFrom).
      fromCliVersion: plan.versions.from.cli,
    }));
    await backup.capture(await externalWriteTargets(projectDir, plan));
    logger.info(`Backup: ${chalk.cyan(`.smartstack/upgrade-backups/${backup.id}/`)} (undo with ${chalk.cyan('ss upgrade --restore latest')})`);
    console.log();
  }

  // NuGet — the SmartStack.* family, planned by the preflight
  const allProjects = await findAllProjects(projectDir);

  // Restore once and, when NuGet reports a downgrade (NU1605), auto-align the offending
  // direct references to the new transitive floor and re-restore. Mutates `result`.
  const restoreAndRepair = async (): Promise<void> => {
    const restore = execCapture('dotnet restore', projectDir);
    if (restore.ok) {
      result.restoreFailed = false; // last restore is authoritative
      return;
    }
    const repair = await repairNuGetDowngrades(restore.output, allProjects, projectDir, dryRun);
    result.packagesAligned += repair.aligned.length;
    if (repair.resolved) {
      result.restoreFailed = false; // last restore is authoritative
      logger.success(`Restore recovered — aligned ${chalk.cyan(repair.aligned.length)} package(s) to SmartStack's dependency floor`);
      return;
    }
    result.restoreFailed = true;
    if (repair.unresolved.length > 0) {
      result.unresolvedDowngrades = [...new Set([...result.unresolvedDowngrades, ...repair.unresolved])];
    }
    logger.error('dotnet restore failed — see the NuGet error(s) above');
  };

  if (plan.packageWrites.length === 0) {
    logger.success(`SmartStack packages already at ${chalk.cyan(socleTo)}`);
    result.nugetSkipped = allProjects.filter((p) => p.currentVersion !== null).length;
  } else {
    logger.info(`Bumping ${plan.packageEdits.length} SmartStack package reference(s) to ${chalk.cyan(socleTo)}...`);
    for (const write of plan.packageWrites) {
      if (dryRun) {
        logger.info(`  [DRY RUN] would rewrite ${write.rel}`);
      } else {
        await backup.write(write.rel, write.after);
        logger.info(`  ${chalk.green('↑')} ${write.rel}`);
      }
    }
    result.nugetUpgraded = plan.packageWrites.length;
    if (!dryRun) {
      logger.info('Restoring NuGet packages...');
      await restoreAndRepair();
    }
  }
  console.log();

  if (allProjects.length === 0) {
    logger.warning('No .NET projects found in src/ directory.');
  } else {
    // Ensure required packages are present (reconcile older projects)
    logger.info('Checking required NuGet packages...');
    for (const project of allProjects) {
      project.csprojContent = await fs.readFile(project.csprojPath, 'utf-8');
    }
    const requiredResult = await ensureRequiredPackages(allProjects, baseNamespace, dryRun);
    result.requiredPkgAdded = requiredResult.added.length;
    result.requiredPkgFailed = requiredResult.failed.length;

    if (requiredResult.added.length > 0) {
      logger.success(`${requiredResult.added.length} required package(s) added`);
      if (!dryRun) {
        logger.info('Restoring NuGet packages...');
        await restoreAndRepair();
      }
    } else {
      logger.info(`Required packages ${chalk.green('✓')} all present`);
    }
    if (requiredResult.failed.length > 0) {
      logger.error(`${requiredResult.failed.length} required package(s) failed to add`);
    }
    console.log();

    // Upgrade other NuGet packages only if --all-packages is specified
    if (options.allPackages) {
      logger.info('Checking other NuGet packages...');
      for (const project of allProjects) {
        const content = await fs.readFile(project.csprojPath, 'utf-8');
        const otherPackages = readAllPackageReferences(content).filter(p => !/^SmartStack(\.|$)/i.test(p.name));

        if (otherPackages.length === 0) continue;

        logger.info(`  ${chalk.cyan(project.relPath)}:`);
        for (const pkg of otherPackages) {
          try {
            if (dryRun) {
              logger.info(`    [DRY RUN] ${pkg.name} ${chalk.yellow(pkg.currentVersion)} → latest`);
              result.otherPkgUpdated++;
              continue;
            }

            execSync(
              `dotnet add "${project.csprojPath}" package ${pkg.name} --no-restore`,
              { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }
            );

            const newVersion = readPackageVersion(await fs.readFile(project.csprojPath, 'utf-8'), pkg.name);
            if (newVersion && newVersion !== pkg.currentVersion) {
              logger.success(`    ${chalk.green('✓')} ${pkg.name} ${chalk.yellow(pkg.currentVersion)} → ${chalk.cyan(newVersion)}`);
              result.otherPkgUpdated++;
            } else {
              result.otherPkgSkipped++;
            }
          } catch (error) {
            result.otherPkgFailed++;
            logger.error(`    ${chalk.red('✗')} ${pkg.name}: ${error instanceof Error ? error.message : error}`);
          }
        }

        if (!dryRun && (result.otherPkgUpdated > 0 || result.otherPkgFailed > 0)) {
          logger.info(`  Restoring ${chalk.cyan(project.relPath)}...`);
          tryExecCommand(`dotnet restore "${project.csprojPath}"`, undefined);
        }
      }
      if (result.otherPkgUpdated > 0) logger.success(`${result.otherPkgUpdated} package(s) updated`);
      if (result.otherPkgSkipped > 0) logger.info(`${result.otherPkgSkipped} package(s) already at latest`);
      if (result.otherPkgFailed > 0) logger.error(`${result.otherPkgFailed} package(s) failed to update`);
      console.log();
    } else {
      logger.info(`Other NuGet packages: ${chalk.gray('skipped')} (use ${chalk.cyan('--all-packages')} to upgrade all)`);
      console.log();
    }
  }

  // Frontend — the npm target FOLLOWS the NuGet target (no fallback tag, ever)
  if (!frontendDir) {
    logger.warning(`No frontend directory found with ${SOCLE_NPM_NAME} package.`);
  } else {
    const relFrontendPath = frontendDir.replace(projectDir, '').replace(/^[/\\]/, '');
    if (plan.npmUpToDate) {
      logger.info(`Frontend: ${chalk.cyan(relFrontendPath)} ${chalk.green('✓')} already at ${chalk.cyan(npmTo ?? '?')}`);
      result.npmSkipped = true;
    } else if (npmTo) {
      logger.info(`Frontend: ${chalk.cyan(relFrontendPath)} (${chalk.yellow(plan.versions.from.npm ?? '?')} → ${chalk.cyan(npmTo)})`);
      if (tryExecCommand(`npm install ${SOCLE_NPM_NAME}@${npmTo}`, frontendDir, dryRun)) {
        logger.success(`npm package upgraded to ${chalk.cyan(npmTo)}`);
        result.npmUpgraded = true;
      } else {
        result.npmFailed = true;
        reportErrors.push(`npm install ${SOCLE_NPM_NAME}@${npmTo} failed`);
        logger.error(`npm install ${SOCLE_NPM_NAME}@${npmTo} failed — no other version is tried (the npm package must match NuGet ${socleTo}). Retry later or run it by hand.`);
      }
    }

    if (options.allPackages) {
      logger.info('Updating other npm packages...');
      if (tryExecCommand('npm update', frontendDir, dryRun)) {
        logger.success('npm packages updated');
        result.npmOtherUpdated = true;
      } else {
        logger.error('Failed to update npm packages');
      }
    } else {
      logger.info(`Other npm packages: ${chalk.gray('skipped')} (use ${chalk.cyan('--all-packages')} to upgrade all)`);
    }

    logger.info('Syncing test-frontend templates...');
    result.testFrontendSynced = await syncTestFrontendTemplates(frontendDir, dryRun, backup);
    if (result.testFrontendSynced > 0) logger.success(`${result.testFrontendSynced} test template(s) deployed`);
    else logger.info(`Test templates ${chalk.green('✓')} up to date`);

    logger.info('Syncing postinstall theme patcher...');
    await syncFrontendPostinstallScript(frontendDir, dryRun, backup);

    result.i18nBundlesRelocated = await relocatePhantomI18nLocales(frontendDir, dryRun, backup);
    if (result.i18nBundlesRelocated > 0) {
      logger.success(`${result.i18nBundlesRelocated} i18n bundle(s) recovered from the phantom web/ tree`);
    }

    result.adminFallbacksCleaned = await syncPlatformAdminFallbacks(frontendDir, dryRun, backup);
    console.log();
  }

  // Code migrations — re-run on the CURRENT content (npm install may have rewritten package.json
  // since the preflight computed them); the preflight already proved none of them throws.
  logger.info('Applying code migrations...');
  const migrations = findApplicableMigrations({
    socle: { from: plan.versions.from.socle, to: socleTo },
    cli: { from: plan.versions.from.cli, to: cliVersion },
  });
  const codemods = dryRun ? { files: plan.codemods.files, errors: [] } : await planCodemods(projectDir, migrations);
  for (const e of codemods.errors) {
    reportErrors.push(e.message);
    logger.error(e.message);
  }
  for (const file of codemods.files) {
    if (!dryRun) await backup.write(file.rel, file.after);
    logger.success(`${dryRun ? '[DRY RUN] Would migrate' : 'Migrated'}: ${file.rel} (${file.descriptions.join('; ')})`);
  }
  result.codemodsApplied = codemods.files.length;
  if (codemods.files.length === 0) logger.info('No code migrations needed.');
  console.log();

  // .gitattributes — generated state files merge as binary (append the missing lines only, through the backup).
  const attrsPath = join(projectDir, '.gitattributes');
  const attrs = planGitattributes((await fs.pathExists(attrsPath)) ? await fs.readFile(attrsPath, 'utf-8') : null);
  if (attrs) {
    if (!dryRun) await backup.write('.gitattributes', attrs.content);
    logger.success(`${dryRun ? '[DRY RUN] Would declare' : 'Declared'} ${attrs.added.length} generated state path(s) merge=binary in .gitattributes`);
    console.log();
  }

  // Product version of the client (root package.json → Directory.Build.props <VersionPrefix> →
  // assemblies → the platform's user menu and environment banner). Before the docker sync: the
  // Dockerfile copies the root package.json into the build stage.
  logger.info('Checking the product version wiring...');
  const versioningChanges = await syncClientVersioning(projectDir, baseNamespace.toLowerCase(), dryRun, backup);
  if (versioningChanges.length > 0) {
    for (const line of versioningChanges) logger.info(`  ${chalk.green('+')} ${line}`);
    if (dryRun) logger.warning(`[DRY RUN] Would wire the product version (${versioningChanges.length} change(s))`);
    else logger.success('Product version wired — the API now reports it (user menu, environment banner)');
  } else {
    logger.info(`Product version ${chalk.green('✓')} wired`);
  }
  console.log();

  // Register client extension entities for the platform global search (idempotent, fail-soft).
  await syncExtensionSearchRegistration(projectDir, baseNamespace, dryRun);
  console.log();

  // Back-fill the HR time-entry imputation seam (idempotent, fail-soft — never auto-registers).
  await syncTimeEntryRefsRegistration(projectDir, baseNamespace, dryRun);
  console.log();

  // Back-fill the export-datasets seam (idempotent, fail-soft — no scaffolder, nothing to register).
  await syncExportDatasetsRegistration(projectDir, baseNamespace, dryRun, backup);
  console.log();

  // appsettings.json / .claude/settings.json — planned (and parsed as JSONC) by the preflight
  logger.info('Syncing appsettings.json...');
  if (plan.appsettings && !dryRun && !(await applyJsonPlan(projectDir, plan.appsettings, backup))) plan.appsettings = null;
  result.configSynced = logJsonSync('appsettings.json', plan.appsettings, dryRun);

  logger.info('Syncing .claude/settings.json...');
  if (plan.claudeSettings && !dryRun && !(await applyJsonPlan(projectDir, plan.claudeSettings, backup))) plan.claudeSettings = null;
  result.claudeSettingsSynced = logJsonSync('.claude/settings.json', plan.claudeSettings, dryRun);

  for (const skipped of plan.skippedJsonc) {
    logger.warning(`${skipped.rel} NOT rewritten — ${skipped.reason}. Pending:`);
    for (const line of skipped.pending) console.log(`    ${line}`);
  }

  // User-level settings (outside the project — not part of the project backup)
  if (!dryRun) {
    try {
      const mcpCleanup = await removeMcpServerRegistration();
      if (mcpCleanup.cleaned.length > 0) {
        for (const path of mcpCleanup.cleaned) logger.success(`Removed legacy MCP entries from ${path}`);
      } else if (mcpCleanup.errors.length === 0) {
        logger.info(`Legacy MCP entries ${chalk.green('✓')} not found`);
      }
      for (const err of mcpCleanup.errors) logger.warning(`MCP cleanup: ${err}`);
    } catch (error) {
      logger.warning(`MCP cleanup failed: ${error instanceof Error ? error.message : 'unknown'}`);
    }
    try {
      const envResult = await configureClaudeEnvironment();
      if (envResult.success) logger.info(`Claude Code environment ${chalk.green('✓')} MAX_OUTPUT_TOKENS=64000`);
      else logger.warning(`Failed to configure Claude environment: ${envResult.error}`);
    } catch {
      // Non-critical
    }
    console.log();
  }

  // Docker files — never silently overwrite a client's local edits.
  logger.info('Syncing docker files...');
  let initState = await loadInitState(projectDir);
  if (!initState) {
    // Project predates state tracking entirely — synthetic state, persisted
    // only if the sync records at least one hash. Its cliVersion is the CLI's.
    initState = createCompletedInitState(config, cliVersion);
  }
  // The compose's previous-certificate slot is valid from SmartStack 3.70 only (an
  // empty AdditionalCertificates entry fails startup before). Gate on the version the
  // project REFERENCES after the NuGet step — a failed bump keeps the old one, and the
  // slot with it stays out; a dry-run judges by the version it would install.
  const platformVersion = dryRun ? socleTo : await readProjectSmartStackVersion(projectDir);
  const slotSupported = supportsEmptyCertificateSlot(platformVersion);
  const dockerSummary = await syncDockerFiles({
    projectDir,
    projectName: baseNamespace,
    templatesDir: TEMPLATES_DIR,
    state: initState,
    dryRun,
    force: options.forceDocker ?? false,
    blocks: { [ADDITIONAL_CERTIFICATE_SLOT_BLOCK]: slotSupported },
  });
  for (const fileResult of dockerSummary.files) logDockerFileResult(fileResult, dryRun);
  result.dockerSynced = dockerSummary.written;
  result.dockerSkippedModified = dockerSummary.skippedModified;
  if (!dryRun && dockerSummary.stateChanged) {
    initState.updatedAt = new Date().toISOString();
    await saveInitState(projectDir, initState);
  }
  if (dockerSummary.written > 0) logger.success(`${dockerSummary.written} docker file(s) updated`);
  else if (dockerSummary.skippedModified.length === 0) logger.info(`Docker files ${chalk.green('✓')} up to date`);
  logSkippedModifiedSummary(dockerSummary.skippedModified);
  console.log();

  // DataProtection: back-fill the docker stack's secrets (never overwriting one) and
  // flag the RequireEncryptedKeyRing=false opt-out.
  logger.info('Checking DataProtection certificate...');
  const composeUpdatePending = dryRun && dockerSummary.files.some((f) =>
    f.relPath === 'docker-images/docker-compose.yml' && f.action !== 'up_to_date' && f.action !== 'skipped_modified');
  Object.assign(result, await backfillDockerDataProtection({
    projectDir, projectName: baseNamespace, dryRun, composeUpdatePending, slotSupported,
  }));
  console.log();

  // Clean residual files left by dotnet new (e.g. .http files)
  result.residualsCleaned = await cleanResidualFiles(projectDir, baseNamespace, dryRun, backup);
  if (result.residualsCleaned > 0) {
    logger.success(`${result.residualsCleaned} residual file(s) cleaned`);
    console.log();
  }

  // Fix init-state.json if cliVersion is 0.0.0 (pre-fix bug) — with the CLI version
  result.initStateFixed = await fixInitStateVersion(projectDir, cliVersion, dryRun);
  if (result.initStateFixed) {
    logger.success('Fixed cliVersion in init-state.json');
    console.log();
  }

  // Remove the legacy .ralph/ directory (Ralph was replaced by /ba-develop) — captured above
  if (dryRun) {
    if (await fs.pathExists(join(projectDir, '.ralph'))) {
      logger.info(`[DRY RUN] Would remove legacy ${chalk.cyan('.ralph/')} directory`);
    }
  } else {
    result.ralphPurged = await purgeRalphProjectArtifacts(projectDir);
  }

  // Validate Program.cs has all required SmartStack bootstrap calls
  logger.info('Validating Program.cs...');
  result.programCsIssues = await validateProgramCs(projectDir, baseNamespace);
  if (result.programCsIssues.length > 0) {
    logger.error('Program.cs is missing required SmartStack calls:');
    for (const issue of result.programCsIssues) logger.warning(`  ${chalk.red('✗')} ${issue}`);
    console.log();
    logger.warning('Your Program.cs must contain these 4 calls in order:');
    logger.info(`  1. builder.Services.${chalk.cyan('AddSmartStack')}(builder.Configuration);`);
    logger.info(`  2. await app.${chalk.cyan('InitializeSmartStackAsync')}();`);
    logger.info(`  3. app.${chalk.cyan('UseSmartStack')}();`);
    logger.info(`  4. app.${chalk.cyan('MapSmartStack')}();`);
    console.log();
  } else {
    logger.info(`Program.cs ${chalk.green('✓')} all SmartStack calls present`);
    console.log();
  }

  if (dryRun) {
    logger.info(`[DRY RUN] Would then: regenerate the frontend primitives + component registry${options.regen === false ? ' (skipped: --no-regen)' : ''}, ` +
      `sync the skills${options.skipSkills ? ' (skipped: --skip-skills)' : ''}, build/test the project${options.skipVerify ? ' (skipped: --skip-verify)' : ''}, ` +
      `and write ${UPGRADE_REPORT_REL}.`);
    console.log();
    return { status: 'dry-run', exitCode: 0, plan };
  }

  // ── 3. PROJECT-WIDE REGEN (default; --no-regen) ──────────────────────────
  let regen: RegenStepResult[] = [];
  if (options.regen === false) {
    logger.info(`Project regeneration: ${chalk.gray('skipped')} (--no-regen) — run scaffold-ui-primitives + aggregate-component-registry before any page re-scaffold.`);
  } else if (frontendDir) {
    logger.info('Regenerating the frontend primitives and the component registry...');
    regen = await runProjectRegen({
      projectDir,
      frontendDir,
      appCode: baseNamespace.toLowerCase(),
      skillsDir: join(PACKAGE_ROOT, 'templates', 'skills'),
      toolCwd: PACKAGE_ROOT,
      backup,
    });
    for (const step of regen) {
      if (step.ok) logger.success(`  ${step.name} ${chalk.green('✓')}`);
      else {
        reportWarnings.push(`${step.name}: ${step.message}`);
        logger.warning(`  ${step.name}: ${step.message}`);
      }
    }
  }
  console.log();

  // ── 4. BOOKKEEPING — versions (never the NuGet version as cliVersion) ────
  if (!result.restoreFailed) {
    await writeConfig({ ...config, smartStackVersion: socleTo });
    logger.success(`Config SmartStack version → ${chalk.cyan(socleTo)}`);
  } else {
    logger.warning(`Config version left at ${chalk.yellow(plan.versions.from.socle ?? '?')} — restore is broken; fix it, then re-run ${chalk.cyan('ss upgrade')} to record ${chalk.cyan(socleTo)}`);
  }

  // ── 5. SKILLS CHAIN (--skip-skills) ──────────────────────────────────────
  const skills = { before: await readInstalledSkillsVersion(), after: null as string | null, updated: false, skipped: false, error: null as string | null };
  if (options.skipSkills) {
    skills.skipped = true;
    skills.after = skills.before;
    if (skills.before !== cliVersion) logger.warning(`Skills (${skills.before ?? 'not installed'}) ≠ CLI ${cliVersion} — not synced (--skip-skills). Run ${chalk.cyan('ss update')}.`);
  } else if (skills.before === cliVersion) {
    skills.after = skills.before;
    logger.info(`Skills ${chalk.green('✓')} at ${cliVersion}`);
  } else {
    logger.info(`Syncing the skills (${skills.before ?? 'not installed'} → ${cliVersion})...`);
    try {
      await updateCommands({ global: true });
      skills.updated = true;
    } catch (err) {
      skills.error = err instanceof Error ? err.message : String(err);
      reportWarnings.push(`skills sync failed: ${skills.error} — run ss update`);
      logger.warning(`Skills sync failed: ${skills.error} — run ${chalk.cyan('ss update')}`);
    }
    skills.after = await readInstalledSkillsVersion();
  }
  await writeConfig(withGenerator(config, { cliVersion, skillsVersion: skills.after }));
  console.log();

  // ── 6. VERIFY (--skip-verify) ────────────────────────────────────────────
  let verify: VerifyResult | null = null;
  if (options.skipVerify) {
    logger.info(`Post-upgrade verification: ${chalk.gray('skipped')} (--skip-verify)`);
  } else {
    const { steps, notes } = await planVerifySteps(projectDir, frontendDir);
    logger.info('Post-upgrade verification (build + tests)...');
    verify = runVerify(steps, undefined, (step) => logger.info(`  → ${step.name}`));
    verify.notes = notes;
    for (const note of notes) logger.info(chalk.gray(`  ${note}`));
    for (const step of verify.steps) {
      if (step.ok) logger.success(`  ${step.name} ${chalk.green('✓')}`);
      else {
        reportErrors.push(`${step.name} failed`);
        logger.error(`  ${step.name} ${chalk.red('✗')}`);
        if (step.outputTail) console.log(chalk.gray(step.outputTail.split('\n').map((l) => `      ${l}`).join('\n')));
      }
    }
    result.verifyFailed = !verify.ok;
  }
  console.log();

  // ── 6b. LIFECYCLE — the developed modules to resume (audit-lifecycle) ────
  // The new generators reach a module only through /ba-develop: the lifecycle
  // audit (one engine, run as a child CLI) names them — and the code written
  // outside protected regions that such a resume would erase.
  const lifecycle: LifecycleBridgeResult = await runLifecycleAudit({
    projectDir,
    skillsDir: join(PACKAGE_ROOT, 'templates', 'skills'),
    toolCwd: PACKAGE_ROOT,
    rules: UPGRADE_RULES,
  });
  const resume: ModuleToResume[] = lifecycle.ran ? modulesToResume(lifecycle.findings) : [];
  if (!lifecycle.ran) {
    logger.info(chalk.gray(`Lifecycle audit not run (${lifecycle.reason}) — run /audit-lifecycle to list the modules to resume.`));
  } else if (resume.length > 0) {
    logger.info(`Developed modules to resume with the new generators: ${resume.map((m) => chalk.cyan(`${m.app}/${m.module}`)).join(', ')}`);
  } else {
    logger.info(`Developed modules: ${chalk.green('✓')} none to resume`);
  }
  for (const f of lifecycle.findings.filter((x) => x.severity !== 'ok' && !['LIFE-001', 'LIFE-002'].includes(x.ruleId))) {
    reportWarnings.push(`${f.ruleId}: ${f.message}`);
    logger.warning(`  ${f.ruleId}: ${f.message}`);
  }
  console.log();

  // ── 7. STATUS + REPORT ───────────────────────────────────────────────────
  const outcome = computeUpgradeOutcome(result);
  const status: UpgradeStatus = outcome.hasFailures ? 'partial' : 'ok';
  await writeConfig(withLastUpgrade(config, {
    cliVersion,
    socleVersion: result.restoreFailed ? plan.versions.from.socle : socleTo,
    npmVersion: result.npmFailed ? plan.versions.from.npm : npmTo,
    at: new Date().toISOString(),
    status,
    backupId: backup.id,
  }));

  const nextSteps: string[] = [];
  if (status === 'partial') nextSteps.push('Fix the errors above, or undo the whole upgrade: ss upgrade --restore latest');
  if (plan.skippedJsonc.length > 0) nextSteps.push('Apply the pending configuration changes listed in skipped[] (files with comments).');
  if (resume.length > 0) {
    for (const m of resume) nextSteps.push(`Resume ${m.app}/${m.module} with the new generators: /ba-develop ${m.app}/${m.module} (the evolution plan re-runs only what changed; written regions are kept).`);
  } else if (!lifecycle.ran) {
    nextSteps.push('Resume the developed modules with the new generators: /audit-lifecycle lists them, then /ba-develop <APP>/<MODULE> (resume).');
  }
  nextSteps.push('Review: git diff — then commit through /gitflow commit.');

  const undeclared = plan.git?.isRepo && plan.git.dirty.length === 0
    ? checkGitSafety(projectDir, { allowDirty: true, allowProtected: true }).dirty
      .filter((rel) => !backup.isCaptured(rel) && rel !== UPGRADE_REPORT_REL)
    : [];
  if (undeclared.length > 0) {
    reportWarnings.push(`files changed outside the backup: ${undeclared.join(', ')}`);
    logger.warning(`${undeclared.length} file(s) changed by a tool outside the upgrade backup (not restorable by --restore): ${undeclared.join(', ')}`);
  }

  const report = {
    schemaVersion: 1,
    status,
    startedAt,
    finishedAt: new Date().toISOString(),
    cliVersion,
    from: plan.versions.from,
    to: plan.versions.to,
    backupId: backup.hasFiles ? backup.id : null,
    git: plan.git ? { branch: plan.git.branch, branchType: plan.git.branchType, head: plan.git.head } : null,
    packageEdits: plan.packageEdits,
    codemods: codemods.files.map((f) => ({ file: f.rel, migrations: f.descriptions })),
    config: {
      appsettings: plan.appsettings ? { added: plan.appsettings.added, removed: plan.appsettings.removed, relocated: plan.appsettings.relocated, corrected: plan.appsettings.corrected } : null,
      claudeSettings: plan.claudeSettings ? { added: plan.claudeSettings.added } : null,
    },
    skipped: plan.skippedJsonc,
    docker: { written: result.dockerSynced, skippedModified: result.dockerSkippedModified, secretsProvisioned: result.dockerSecretsProvisioned },
    regen,
    skills,
    verify,
    lifecycle: { ran: lifecycle.ran, ...(lifecycle.reason ? { reason: lifecycle.reason } : {}), findings: findingLines(lifecycle.findings) },
    modulesToResume: resume,
    errors: reportErrors,
    warnings: reportWarnings,
    undeclaredWrites: undeclared,
    nextSteps,
  };
  await backup.write(UPGRADE_REPORT_REL, JSON.stringify(report, null, 2) + '\n');
  const manifest: BackupManifest | null = await backup.finalize();

  // ── Summary ──────────────────────────────────────────────────────────────
  const lines: string[] = [outcome.title, ''];
  if (result.nugetUpgraded > 0) lines.push(`  ${chalk.green('✓')} SmartStack: ${plan.packageEdits.length} reference(s) in ${result.nugetUpgraded} file(s) → ${chalk.cyan(socleTo)}`);
  else lines.push(`  ${chalk.gray('–')} SmartStack: already at ${chalk.cyan(socleTo)}`);
  if (result.requiredPkgAdded > 0) lines.push(`  ${chalk.green('✓')} Required packages: ${result.requiredPkgAdded} added`);
  if (result.packagesAligned > 0) lines.push(`  ${chalk.green('✓')} Aligned ${result.packagesAligned} direct reference(s) to SmartStack's dependency floor (NU1605)`);
  if (result.otherPkgUpdated > 0) lines.push(`  ${chalk.green('✓')} Other NuGet: ${result.otherPkgUpdated} package(s) updated`);
  if (result.otherPkgFailed > 0) lines.push(`  ${chalk.red('✗')} Other NuGet: ${result.otherPkgFailed} package(s) failed`);
  if (result.npmUpgraded) lines.push(`  ${chalk.green('✓')} npm: ${SOCLE_NPM_NAME} → ${chalk.cyan(npmTo)}`);
  if (result.npmSkipped) lines.push(`  ${chalk.gray('–')} npm: already up to date`);
  if (result.npmFailed) lines.push(`  ${chalk.red('✗')} npm: ${SOCLE_NPM_NAME}@${npmTo} could not be installed`);
  if (result.npmOtherUpdated) lines.push(`  ${chalk.green('✓')} npm: other packages updated`);
  if (result.codemodsApplied > 0) lines.push(`  ${chalk.green('✓')} Code migrations: ${result.codemodsApplied} file(s)`);
  if (result.configSynced > 0) lines.push(`  ${chalk.green('✓')} Config: ${result.configSynced} setting(s) reconciled`);
  if (result.claudeSettingsSynced > 0) lines.push(`  ${chalk.green('✓')} Claude: ${result.claudeSettingsSynced} new setting(s) added`);
  if (plan.skippedJsonc.length > 0) lines.push(`  ${chalk.yellow('⚠')} Not rewritten (comments): ${plan.skippedJsonc.map((s) => s.rel).join(', ')} — use ${chalk.cyan('--allow-comment-loss')}`);
  if (result.dockerSynced > 0) lines.push(`  ${chalk.green('✓')} Docker: ${result.dockerSynced} file(s) updated`);
  if (result.dockerSkippedModified.length > 0) lines.push(`  ${chalk.yellow('⚠')} Docker: ${result.dockerSkippedModified.length} file(s) skipped (locally modified — use ${chalk.cyan('--force-docker')})`);
  if (result.dockerSecretsProvisioned.length > 0) lines.push(`  ${chalk.green('✓')} Docker secrets: ${result.dockerSecretsProvisioned.join(', ')} provisioned in ${DOCKER_ENV_REL_PATH} — back it up in a vault`);
  if (result.keyRingOptOuts.length > 0 || result.dockerComposeLacksCertificate) lines.push(...keyRingSummaryLines(result));
  if (result.testFrontendSynced > 0) lines.push(`  ${chalk.green('✓')} Test templates: ${result.testFrontendSynced} file(s) deployed`);
  if (result.adminFallbacksCleaned > 0) lines.push(`  ${chalk.green('✓')} Admin pages: platform-admin fallback registrations removed`);
  if (result.residualsCleaned > 0) lines.push(`  ${chalk.green('✓')} Cleanup: ${result.residualsCleaned} residual file(s) removed`);
  if (result.ralphPurged) lines.push(`  ${chalk.green('✓')} Legacy .ralph/ removed (replaced by /ba-develop)`);
  if (regen.length > 0) lines.push(`  ${regen.every((r) => r.ok) ? chalk.green('✓') : chalk.yellow('⚠')} Regen: ${regen.map((r) => `${r.name} ${r.ok ? 'ok' : 'failed'}`).join(', ')}`);
  if (skills.updated) lines.push(`  ${chalk.green('✓')} Skills: ${skills.before ?? 'none'} → ${skills.after ?? '?'}`);
  if (verify) lines.push(`  ${verify.ok ? chalk.green('✓') : chalk.red('✗')} Verify: ${verify.steps.map((s) => `${s.name} ${s.ok ? 'ok' : 'FAILED'}`).join(', ') || 'nothing to run'}`);
  if (resume.length > 0) lines.push(`  ${chalk.yellow('↻')} To resume: ${resume.map((m) => `${m.app}/${m.module}`).join(', ')}`);
  if (result.requiredPkgFailed > 0) lines.push(`  ${chalk.red('✗')} Required packages: ${result.requiredPkgFailed} failed to add`);
  if (result.restoreFailed) lines.push(`  ${chalk.red('✗')} NuGet restore failed — packages were edited but the solution does not restore`);
  if (result.programCsIssues.length > 0) lines.push(`  ${chalk.red('⚠')} Program.cs: ${result.programCsIssues.length} missing call(s) - manual fix required`);
  lines.push('');
  lines.push(`  Backup: ${manifest ? chalk.cyan(`.smartstack/upgrade-backups/${manifest.id}/`) : chalk.gray('none (nothing changed)')}`);
  lines.push(`  Report: ${chalk.cyan(UPGRADE_REPORT_REL)}`);
  lines.push('');
  lines.push(chalk.yellow('Next steps:'));
  if (result.restoreFailed) {
    const conflict = result.unresolvedDowngrades.length > 0
      ? `align the conflicting version(s): ${result.unresolvedDowngrades.join(', ')}`
      : 'align the conflicting versions';
    lines.push(`  ${chalk.red('!')} Fix the NuGet error above (${conflict}), then run ${chalk.cyan('dotnet restore')}`);
  }
  if (result.programCsIssues.length > 0) lines.push(`  ${chalk.red('!')} Fix Program.cs (see warnings above)`);
  for (const step of nextSteps) lines.push(`  • ${step}`);
  logger.box(lines, outcome.boxStyle);

  if (outcome.exitCode !== 0) process.exitCode = outcome.exitCode;
  return { status, exitCode: outcome.exitCode, plan, backupId: manifest?.id ?? null };
}

export const upgradeCommand = new Command('upgrade')
  .description('Upgrade a SmartStack project: NuGet/npm socle, configuration, code migrations, regeneration, skills — with a git guard, a backup and a verification')
  .option('--preview', 'Upgrade to latest preview/prerelease version')
  .option('--dry-run', 'Show what would be upgraded without writing anything')
  .option('--version <version>', 'Target this SmartStack version (must be published on NuGet) instead of the latest')
  .option('--allow-downgrade', 'Allow a target older than the version the project references')
  .option('--allow-dirty', 'Upgrade even with uncommitted changes in the working tree')
  .option('--allow-protected-branch', 'Upgrade on main/master, release/* or hotfix/* without the interactive confirmation')
  .option('--allow-comment-loss', 'Rewrite JSONC files (appsettings.json, .claude/settings.json) even if their comments are lost')
  .option('--all-packages', 'Also upgrade all other NuGet packages (slow, disabled by default)')
  .option('--force-docker', 'Overwrite locally modified docker files (a .bak backup of each is kept)')
  .option('--skip-skills', 'Do not sync the deployed skills (~/.claude) to this CLI version')
  .option('--skip-verify', 'Do not build/test the project after the upgrade')
  .option('--no-regen', 'Do not regenerate the frontend primitives and the component registry')
  .option('--restore [id]', 'Undo an upgrade from its backup (default: latest) — files changed since are kept')
  .option('--list-backups', 'List the upgrade backups of this project')
  .action(async (options: UpgradeOptions) => {
    const outcome = await runUpgrade(options);
    if (outcome.exitCode !== 0) process.exitCode = outcome.exitCode;
  });
